import { ChangeDetectionStrategy, Component, DestroyRef, AfterViewInit } from '@angular/core';
import { IonContent } from '@ionic/angular/standalone';
import { ButtonComponent } from '@examdojo/core/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Rive } from '@rive-app/canvas';
import { gsap } from 'gsap';
import { RiveDirective } from '@examdojo/ui/rive';
import { TranslocoPipe } from '@jsverse/transloco';
import { connectState } from '@examdojo/angular/util';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FullscreenFlowStepComponent } from '../../../../shared/fullscreen-flow';
import { StreakQuery, StreakService, StreakWeekComponent } from '../../../../streak';

@Component({
  selector: 'dojo-streak-completion',
  templateUrl: './streak-completion.component.html',
  styleUrls: ['./streak-completion.component.scss'],
  standalone: true,
  host: { class: 'ion-page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonContent, RiveDirective, ButtonComponent, StreakWeekComponent, TranslocoPipe],
})
export class StreakCompletionComponent extends FullscreenFlowStepComponent implements AfterViewInit {
  constructor(
    private readonly streakService: StreakService,
    private readonly streakQuery: StreakQuery,
    private readonly destroyRef: DestroyRef,
  ) {
    super();
  }

  private readonly fireRiveReady$$ = new BehaviorSubject<boolean>(false);
  private fireRive!: Rive;

  // DOM elements
  fireCanvas!: HTMLCanvasElement;
  streakCounterText!: HTMLDivElement;
  streakCounterSubtext!: HTMLDivElement;
  streakFireContainer!: HTMLDivElement;
  calendarContainer!: HTMLDivElement;

  readonly state = connectState({
    streakDaysNumber: this.streakQuery.streakDays$,
    keepStreakDaysNumber: this.streakQuery.keepStreakDays$,
  });

  ngAfterViewInit(): void {
    // Initialize DOM elements
    this.fireCanvas = document.querySelector('.streak-fire') as HTMLCanvasElement;
    this.streakCounterText = document.querySelector('.streak-counter-text') as HTMLDivElement;
    this.streakCounterSubtext = document.querySelector('.streak-counter-subtext') as HTMLDivElement;
    this.streakFireContainer = document.querySelector('.streak-fire-container') as HTMLDivElement;
    this.calendarContainer = document.querySelector('.calendar-container') as HTMLDivElement;

    // Set initial state
    this.setInitialState();

    this.fireRiveReady$$
      .pipe(
        tap((isReady) => {
          if (isReady) {
            this.runTimeline();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onFireRiveCanvasReady(canvas: Rive): void {
    this.fireRive = canvas;

    const fireTrigger = this.fireRive.stateMachineInputs('State Machine 1')?.find((input) => input.name === 'Trigger');
    fireTrigger?.fire();

    this.fireRiveReady$$.next(true);
  }

  setInitialState(): void {
    this.streakCounterText.style.color = '#454545';
    this.streakCounterSubtext.style.color = '#454545';
    this.streakCounterText.innerText = (this.streakQuery.getProp('streakDays') - 1).toString();
    gsap.set(this.streakFireContainer, { y: 70 });
    gsap.set(this.streakFireContainer, { scale: 1 });
    gsap.set(this.calendarContainer, { scale: 0 });
  }

  runTimeline(): void {
    const timeline = gsap.timeline();
    const keyframes = [0, 1, 3, 3.2];

    const fireTrigger = this.fireRive.stateMachineInputs('State Machine 1')?.find((input) => input.name === 'Trigger');

    timeline
      // Fire animation
      .to(this.fireRive, { onStart: () => fireTrigger?.fire() }, keyframes[0])

      // Text animation
      .to(this.streakCounterText, { color: '#FD9E44', duration: 0.5 }, keyframes[1])
      .to(this.streakCounterSubtext, { color: '#FD9E44', duration: 0.5 }, keyframes[1])
      .to(
        this.streakCounterText,
        {
          innerText: this.streakQuery.getProp('streakDays').toString(),
          duration: 0,
        },
        keyframes[1],
      )

      // Container shift
      .to(this.streakFireContainer, { scale: 0.8, duration: 0.5, ease: 'power2.inOut' }, keyframes[2])
      .to(this.streakFireContainer, { y: 0, duration: 0.5, ease: 'power2.inOut' }, keyframes[3])
      .to(this.calendarContainer, { scale: 1, duration: 0.5, ease: 'power2.inOut' }, keyframes[3]);
  }

  handleNextStep() {
    this.streakService.saveLastStreakDate();

    this.goToNextStep().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
